import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFormatPipe } from '@processpuzzle/base-entity';
import { catchError, defer, EMPTY, filter, Observable, repeat, switchMap, takeWhile } from 'rxjs';
import { AdministratorAccount, isSeedSettled, SeedState, Signup } from '../domain/signup';
import { SignupService } from '../domain/signup.service';

/** How often the seed job is polled. */
const POLL_INTERVAL_MS = 3000;

/**
 * The sixth and final step of a submitted sign-up.
 *
 * **Its own route, `signup/status/:orgKey`, rather than a `mat-step` in the funnel.** Bringing a
 * customer's application up must not delay the form submission. It is nevertheless the sixth
 * customer-visible step: the customer must activate the account after the five data-entry steps.
 * The key is in the URL because it is the only thing this page needs, and the page is reached by a
 * redirect the customer did not type.
 *
 * **It polls, which it did not before.** This page used to render a hardcoded "your application is
 * ready" and a link built from a compiled-in hostname, on the reasoning that the address is
 * deterministic so there was nothing to wait for. The address is deterministic; whether anything
 * answers at it is not. A customer whose seed failed was told their application was ready and sent
 * to a login they had no account for. So the page now reads `GET /signups/{orgKey}` until the seed
 * settles, and says what is actually true at each step.
 *
 * **There is no click-through, and that is the design rather than an omission.** The activation
 * e-mail Keycloak sends *is* the way in: it carries the one-time token that verifies the address
 * and sets the first password, and until both are done there is no credential to log in with. A
 * button beside it would lead to a login screen the customer cannot pass — which is exactly the
 * failure the old hardcoded link produced. `applicationUrl` is still on the model, because the
 * customer portal renders it for somebody who already has a password.
 *
 * **An e-mail that already has an account is invited instead.** One person is one login, so a
 * second subscription with the same address gets Keycloak's organization invitation, and the page
 * says so: accept it, then open the new application from Customer Home with the usual password.
 *
 * **The poll stops on failure as well as on success.** {@link isSeedSettled} is what it tests; a
 * poll that ran until `READY` would run for ever against a job nobody is coming to fix, three
 * a request every three seconds, from a tab the customer left open.
 */
@Component({
  selector: 'pp-signup-confirmation',
  imports: [MatCard, MatCardTitle, MatCardContent, MatIcon, MatProgressSpinner, TranslocoDirective, DateFormatPipe],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.confirmation'">
      <mat-card class="confirmation-card">
        <p class="confirmation-step">{{ t('step') }}</p>
        <mat-card-title>{{ t('title', { orgKey: orgKey() }) }}</mat-card-title>
        <mat-card-content>
          @if (ready() && existingAccount()) {
            <p class="confirmation-status" test-id="signup-status-ready-existing-account">
              <mat-icon>mark_email_unread</mat-icon>
              <span>{{ t('readyExistingAccount') }}</span>
            </p>
            <p class="confirmation-note">{{ t('existingAccountNote') }}</p>
          } @else if (ready()) {
            <p class="confirmation-status" test-id="signup-status-ready">
              <mat-icon>mark_email_unread</mat-icon>
              <span>{{ t('ready') }}</span>
            </p>
            <p class="confirmation-note">{{ t('invitationNote') }}</p>
          } @else if (failed()) {
            <p class="confirmation-status confirmation-status--failed" test-id="signup-status-failed">
              <mat-icon>error_outline</mat-icon>
              <span>{{ t(failureKey()) }}</span>
            </p>
            <p class="confirmation-note">{{ t('supportNote', { orgKey: orgKey() }) }}</p>
          } @else {
            <p class="confirmation-status" test-id="signup-status-provisioning">
              <mat-progress-spinner mode="indeterminate" diameter="24" />
              <span>{{ t(progressKey()) }}</span>
            </p>
            <p class="confirmation-note">{{ t('leaveOpenNote') }}</p>
          }

          @if (trialEndsAt(); as trialEnd) {
            <p class="confirmation-note" test-id="signup-trial-ends">{{ t('trialEnds', { date: trialEnd | ppDate }) }}</p>
          }
        </mat-card-content>
      </mat-card>
    </ng-container>
  `,
  styles: [
    `
      .confirmation-card {
        max-width: 640px;
      }

      .confirmation-status {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .confirmation-step {
        margin: 0 0 8px;
        font-size: 0.85rem;
        font-weight: 500;
        opacity: 0.75;
      }

      .confirmation-status--failed {
        color: var(--mat-sys-error, #b3261e);
      }

      .confirmation-note {
        font-size: 0.9rem;
        opacity: 0.8;
        max-width: 60ch;
      }
    `,
  ],
})
export class SignupConfirmationComponent {
  /** Bound from the route by `withComponentInputBinding()`, which the host application enables. */
  readonly orgKey = input.required<string>();

  private readonly signups = inject(SignupService);

  /**
   * The latest status, refreshed until the seed settles.
   *
   * `takeWhile(..., true)` with the inclusive flag: the terminal reading is the one the page has to
   * render, so it must be emitted before the stream completes.
   *
   * A failed request completes the poll rather than retrying it. `centralHttpErrorInterceptor`
   * already tells the customer the gateway is unreachable, and a page that kept polling through an
   * outage would add a request every three seconds to a service that is already struggling. The
   * page stays on its last reading, which for a fresh visit is the provisioning message — honest,
   * because provisioning may well still be going on.
   */
  private readonly signup = toSignal<Signup | undefined>(
    toObservable(this.orgKey).pipe(
      filter((orgKey): orgKey is string => !!orgKey),
      switchMap((orgKey) => this.poll(orgKey)),
    ),
  );

  readonly ready = computed(() => this.signup()?.seedState === SeedState.READY);
  readonly failed = computed(() => this.signup()?.seedState === SeedState.FAILED);

  /**
   * The e-mail already had an account, so Keycloak sent an organization invitation rather than an
   * activation link: the customer accepts it and keeps the password they have.
   */
  readonly existingAccount = computed(() => this.signup()?.administratorAccount === AdministratorAccount.EXISTING);

  /**
   * Which progress sentence to show, from the step the job is on.
   *
   * A sign-up with no reading yet, or one for a tenant with no seed job at all, gets the generic
   * message: the page cannot name a step it has not been told about, and "we are setting up your
   * application" is true of both.
   */
  readonly progressKey = computed(() => {
    switch (this.signup()?.seedState) {
      case SeedState.PROVISIONING_IDENTITY:
        return 'provisioningIdentity';
      case SeedState.PROVISIONING_DATA:
        return 'provisioningData';
      default:
        return 'provisioning';
    }
  });

  /**
   * Which failure sentence to show.
   *
   * Keyed off `failedStep` rather than off the server's `seedError`, which arrives as one English
   * sentence — see {@link Signup.seedError}. The two steps fail differently from the customer's
   * point of view: after the identity step they have no account, and after the data step they have
   * one and will be able to use it once somebody finishes the job.
   */
  readonly failureKey = computed(() => {
    switch (this.signup()?.failedStep) {
      case SeedState.PROVISIONING_IDENTITY:
        return 'failedIdentity';
      case SeedState.PROVISIONING_DATA:
        return 'failedData';
      default:
        return 'failed';
    }
  });

  /** The raw instant; `ppDate` renders it in the active language rather than in the browser's locale. */
  readonly trialEndsAt = computed(() => this.signup()?.trialEndsAt);

  /**
   * `defer` + `repeat({ delay })` rather than `timer(0, n).pipe(switchMap(...))`.
   *
   * Two differences, and both matter. The first read happens on subscription rather than on the
   * next macrotask, so the page asks immediately instead of showing a spinner for a scheduler
   * tick it did not need. And the delay is measured from the *response*, not from the previous
   * request — so a slow gateway is never given two overlapping polls, which is the failure mode a
   * fixed-rate timer has exactly when the service can least afford it.
   */
  private poll(orgKey: string): Observable<Signup> {
    return defer(() => this.signups.findStatus(orgKey)).pipe(
      repeat({ delay: POLL_INTERVAL_MS }),
      takeWhile((signup) => !isSeedSettled(signup), true),
      catchError(() => EMPTY),
    );
  }
}
